import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, ChevronRight, X, BadgeCheck } from "lucide-react";

/**
 * VerifyNudgeBanner
 * -----------------
 * Renders on the organizer dashboard urging completion of the 4-layer
 * self-serve identity verification. Motivational + specific: shows exact
 * progress (`2 of 4 complete`), the perk the last-step unlocks (green
 * ID-Verified badge), and dismisses per-user via localStorage so we don't
 * nag someone who consciously chose to skip.
 *
 * Never renders when:
 *   - the user isn't an organizer
 *   - already fully verified (kyc_approved=true)
 *   - previously dismissed
 *   - the status probe fails (silently vanishes — don't spam an error toast)
 */
export default function VerifyNudgeBanner({ user }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const storageKey = user?.id ? `avt:verify_nudge_dismissed:${user.id}` : null;

  useEffect(() => {
    if (!user || user.role !== "organizer" || !storageKey) return;
    if (localStorage.getItem(storageKey) === "1") { setDismissed(true); return; }
    api.get("/organizer/verify/status")
      .then(({ data }) => setStatus(data))
      .catch(() => { /* silent */ });
  }, [user, storageKey]);

  if (!status || dismissed) return null;
  if (status.kyc_approved) return null;

  const doneCount = [
    Boolean(status.name.legal_first_name && status.name.legal_last_name),
    status.email.verified,
    status.phone.verified,
    status.identity.status === "verified" && status.identity.name_matches,
  ].filter(Boolean).length;

  const isNearDone = doneCount >= 2;

  const dismiss = () => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="verify-nudge-banner"
      className="mt-6 rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
      style={{
        background: "linear-gradient(135deg, rgba(107,47,186,0.08), rgba(132,204,22,0.08))",
        border: "2px solid rgba(107,47,186,0.25)",
      }}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span
          className="rounded-full p-3 flex-shrink-0"
          style={{ backgroundColor: "rgba(132,204,22,0.15)", color: "#4B7A0A" }}
        >
          {isNearDone ? <BadgeCheck className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
        </span>
        <div className="min-w-0">
          <div className="overline" style={{ color: "var(--avt-purple)" }} data-testid="verify-nudge-headline">
            {isNearDone ? "Almost there" : "Get verified"}
          </div>
          <h3 className="font-serif text-2xl leading-tight mt-1" data-testid="verify-nudge-title">
            {doneCount === 3
              ? "One more step and you'll wear the ID-Verified shield."
              : "Unlock the green ID-Verified shield on every event."}
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }} data-testid="verify-nudge-sub">
            You&rsquo;ve completed <strong>{doneCount} of 4</strong> steps.
            Fully-verified organizers get priority placement in event
            discovery and can accept Stripe payouts.
          </p>
          {/* Segmented progress dots */}
          <div className="mt-3 flex items-center gap-1" data-testid="verify-nudge-progress">
            {[0, 1, 2, 3].map((i) => (
              <span key={i}
                    data-testid={`verify-nudge-dot-${i}`}
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: i < doneCount ? 28 : 14,
                      backgroundColor: i < doneCount ? "var(--avt-purple)" : "rgba(107,47,186,0.18)",
                    }} />
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/organizer/verify"
          data-testid="verify-nudge-cta"
          className="btn-primary rounded-full px-5 py-2.5 inline-flex items-center gap-2 whitespace-nowrap"
        >
          {doneCount === 0 ? "Start verifying" : "Continue"} <ChevronRight className="h-4 w-4" />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="verify-nudge-dismiss"
          className="rounded-full p-2 hover:bg-black/5 transition"
          title="Dismiss"
        >
          <X className="h-4 w-4" style={{ color: "var(--ink-soft)" }} />
        </button>
      </div>
    </div>
  );
}
